/**
 * ReleaseBreakdownView — the unified SELECT popover's Zwolnij confirmation
 * pre-commit breakdown (action-breakdown-remaining-dialogs task 4.4).
 * Mirrors offer-versions/components/lock-breakdown-view.tsx and
 * withdrawal-breakdown-view.tsx: renders the batch-release preview's three
 * outcomes (releasable_hold, queue_exit, unresolvable) as distinct,
 * separately-labelled groups, each with a distinct-nośnik count and a
 * collapsible entry list, reusing the shared
 * OutcomeResultBucket/OutcomeResultEntryRow presentation task 4.1 built.
 *
 * Unlike the lock/withdrawal previews, this popover has no unit-metadata
 * lookup wired (no `useUnitDecisions`-equivalent exists for an arbitrary,
 * possibly cross-offer availability-grid selection) — entries resolve their
 * display label through the popover's own optional `resolveMediaUnitLabel`
 * prop instead, falling back to a generic label when it's unavailable, same
 * fallback the exactly-one-releasable-cell sentence already uses.
 *
 * The preview's only unresolvable case today is a sold reservation
 * (ReservationsService.previewBatchRelease) — a fixed translated message is
 * shown, never the raw `reason` string the server sends (action-breakdown /
 * "SHALL NOT print a raw error identifier"), same convention as the
 * withdrawal/release-rejected dialogs' own unresolvable-sold detail.
 *
 * Traceability: unified-availability-interaction / Unified SELECT popover
 * for free and own-held cells.
 */
import { useTranslation } from 'react-i18next';
import { Button } from '@ooh/ui/components/button';
import type { BadgeProps } from '@ooh/ui/components/badge';
import type { BatchReleasePreviewItem, BatchReleasePreviewResponse } from '@ooh/contracts';
import {
  BreakdownGroupChip,
  distinctUnitCount,
  OutcomeResultBucket,
  OutcomeResultEntryRow,
  type LooseT,
  type OutcomeResultUnitLookup,
} from '../../reservations/components/outcome-result-buckets';
import { formatDisplayDateRange } from '@ooh/ui/lib/date-format';

/** Display order for the release preview's outcome groups — an outcome with
 * no entries is omitted rather than shown as a zero count (action-breakdown
 * / "Where an outcome applies to no entry, its count SHALL be omitted"). */
const RELEASE_BREAKDOWN_OUTCOME_ORDER: BatchReleasePreviewItem['outcome'][] = [
  'releasable_hold',
  'queue_exit',
  'unresolvable',
];

const RELEASE_BREAKDOWN_LABEL_KEY: Record<BatchReleasePreviewItem['outcome'], string> = {
  releasable_hold: 'availability.select.confirmReleaseBreakdown.releasableLabel',
  queue_exit: 'availability.select.confirmReleaseBreakdown.queueExitLabel',
  unresolvable: 'availability.select.confirmReleaseBreakdown.unresolvableLabel',
};

// A queue exit is a normal outcome, not an error (action-breakdown / "One
// outcome vocabulary across fan-out actions") — only unresolvable is styled
// destructive; a hold's release is the intended, successful outcome.
const RELEASE_BREAKDOWN_BADGE_VARIANT: Record<
  BatchReleasePreviewItem['outcome'],
  BadgeProps['variant']
> = {
  releasable_hold: 'success',
  queue_exit: 'warning',
  unresolvable: 'destructive',
};

interface ReleaseBreakdownGroup {
  outcome: BatchReleasePreviewItem['outcome'];
  entries: BatchReleasePreviewItem[];
}

/**
 * Merges one hold's duplicate per-cell `releasable_hold` preview items into a single
 * entry (browser QA, CU-86cbh03pj) — the preview response has one item PER SELECTED
 * CELL, not per hold (`buildSelectedPreviewItems`/`previewReleaseCell`,
 * `reservations.service.ts`), so a day-granularity 11-day selection of one hold
 * produced 11 near-identical rows ("21 lip–21 lip", "22 lip–22 lip", ...) instead of
 * the one row naming the true released range — directly contradicting the group
 * chip's own `distinctUnitCount`, which already counts distinct nośniki, not cells.
 * `releasedRange` is the authoritative per-hold identity when present (mode='selected'
 * under-coverage — every cell belonging to the same hold carries the SAME
 * `releasedRange`/`remainingRange(s)`, stamped by `resolvePartialReleaseOutcome`);
 * falls back to `mediaUnitId` alone for mode='full' (or an exact-full-range match),
 * where the preview response carries no per-hold identity field at all — matching the
 * SAME mediaUnitId-based grouping `distinctUnitCount`'s chip count already assumes.
 */
type ReleasableHoldItem = Extract<BatchReleasePreviewItem, { outcome: 'releasable_hold' }>;

function groupReleasableHoldEntries(entries: BatchReleasePreviewItem[]): BatchReleasePreviewItem[] {
  const groups = new Map<string, ReleasableHoldItem[]>();
  for (const entry of entries) {
    if (entry.outcome !== 'releasable_hold') continue;
    const key = entry.releasedRange
      ? `${entry.mediaUnitId}:${entry.releasedRange.from}:${entry.releasedRange.to}`
      : `${entry.mediaUnitId}`;
    const group = groups.get(key);
    if (group) {
      group.push(entry);
    } else {
      groups.set(key, [entry]);
    }
  }
  return [...groups.values()].map(mergeReleasableHoldGroup);
}

/**
 * Collapses one hold's grouped cell-entries into the single entry displayed for it.
 * When the group carries a server-resolved `releasedRange` (mode='selected'), every
 * member shares it by construction (see `groupReleasableHoldEntries`'s grouping key)
 * — the first entry is already authoritative. Otherwise (mode='full', no
 * `releasedRange`), resolves the displayed window as the min-to-max span across the
 * group's own selected-cell windows — the SAME min-max resolution the spec applies
 * elsewhere (Rezerwacja's date-range field), since the raw per-cell window is all the
 * client has for this case.
 */
function mergeReleasableHoldGroup(group: ReleasableHoldItem[]): ReleasableHoldItem {
  const [first, ...rest] = group;
  if (!first) {
    throw new Error('mergeReleasableHoldGroup: empty group');
  }
  if (first.releasedRange || rest.length === 0) return first;
  let minFrom = first.dateFrom;
  let maxTo = first.dateTo;
  for (const entry of rest) {
    if (entry.dateFrom < minFrom) minFrom = entry.dateFrom;
    if (entry.dateTo > maxTo) maxTo = entry.dateTo;
  }
  return { ...first, dateFrom: minFrom, dateTo: maxTo };
}

function groupReleasePreviewItems(items: BatchReleasePreviewResponse): ReleaseBreakdownGroup[] {
  return RELEASE_BREAKDOWN_OUTCOME_ORDER.map((outcome) => {
    const outcomeEntries = items.filter((item) => item.outcome === outcome);
    return {
      outcome,
      // queue_exit and unresolvable stay one row per cell — a queue exit is
      // legitimately per-window (spec: "by nośnik and window", several windows per
      // nośnik are expected and must stay distinct); unresolvable's rarer multi-cell
      // case isn't the reported defect and grouping it risks masking a genuinely
      // distinct sold reservation on the same unit.
      entries:
        outcome === 'releasable_hold' ? groupReleasableHoldEntries(outcomeEntries) : outcomeEntries,
    };
  }).filter((group) => group.entries.length > 0);
}

/**
 * The preview's only unresolvable case today is a sold reservation — a
 * fixed translated message, never the server's raw `reason` string, same
 * convention as `withdrawalUnresolvableDetail`/`releaseBreakdownUnresolvableDetail`
 * in the sibling dialogs task 4.2/4.3 built.
 */
function releaseBreakdownUnresolvableDetail(t: LooseT): string {
  return t('availability.select.confirmReleaseBreakdown.unresolvableSoldReason');
}

/**
 * Names the remaining range(s) a `mode='selected'` `releasable_hold` entry would
 * leave held (CU-86cbh03pj) — `undefined` for a plain full-hold release (mode='full',
 * or an exact-range match under mode='selected'), which carries neither field. Reads
 * `remainingRange`/`remainingRanges` as bare ISO date strings straight into
 * `formatDisplayDateRange`, never `new Date(isoString)` first — see
 * `OutcomeResultEntryRowProps`'s doc comment for why that conversion is unsafe.
 */
function releaseBreakdownRemainingDetail(
  item: BatchReleasePreviewItem,
  t: LooseT,
): string | undefined {
  if (item.outcome !== 'releasable_hold') return undefined;
  if (item.remainingRanges) {
    return t('availability.select.confirmReleaseBreakdown.remainingRangesDetail', {
      rangeA: formatDisplayDateRange(item.remainingRanges[0].from, item.remainingRanges[0].to),
      rangeB: formatDisplayDateRange(item.remainingRanges[1].from, item.remainingRanges[1].to),
    });
  }
  if (item.remainingRange) {
    return t('availability.select.confirmReleaseBreakdown.remainingRangeDetail', {
      range: formatDisplayDateRange(item.remainingRange.from, item.remainingRange.to),
    });
  }
  return undefined;
}

/**
 * Resolves an entry's DISPLAYED window (browser QA, CU-86cbh03pj) — `releasedRange`
 * (a bare ISO date-string pair) when present, since the server now populates it for
 * EVERY `releasable_hold` outcome (see `BatchReleasePreviewItemSchema`'s doc comment)
 * and it's the one field that's always the TRUE released range, unlike `dateFrom`/
 * `dateTo`, which are just the raw selected cell's own window (a single day at
 * day-granularity, or the min-to-max fallback `mergeReleasableHoldGroup` computes when
 * an old API build predates this field). Returns the STRING pair straight through,
 * never `new Date(isoString)` first — see `OutcomeResultEntryRowProps`'s doc comment
 * for why that conversion is unsafe. Non-`releasable_hold` outcomes (`queue_exit`/
 * `unresolvable`) have no `releasedRange` at all and always fall back to their own
 * `dateFrom`/`dateTo`.
 */
function resolveEntryDisplayRange(entry: BatchReleasePreviewItem): {
  from: Date | string;
  to: Date | string;
} {
  if (entry.outcome === 'releasable_hold' && entry.releasedRange) {
    return { from: entry.releasedRange.from, to: entry.releasedRange.to };
  }
  return { from: entry.dateFrom, to: entry.dateTo };
}

export interface ReleaseBreakdownPreview {
  isLoading: boolean;
  isError: boolean;
  data: BatchReleasePreviewResponse | undefined;
  refetch: () => void;
}

export function ReleaseBreakdownView({
  preview,
  lookup,
}: {
  preview: ReleaseBreakdownPreview;
  lookup: OutcomeResultUnitLookup;
}) {
  const { t } = useTranslation('inventory');
  const looseT = t as LooseT;
  const unknownUnitLabel = t('availability.select.confirmReleaseUnknownUnit');

  if (preview.isLoading) {
    return (
      <p
        className="text-sm text-muted-foreground"
        data-testid="unified-select-release-breakdown-loading"
      >
        {t('availability.select.confirmReleaseBreakdown.loading')}
      </p>
    );
  }

  if (preview.isError) {
    return (
      <div className="space-y-2" data-testid="unified-select-release-breakdown-error">
        <p className="text-sm text-destructive">
          {t('availability.select.confirmReleaseBreakdown.error')}
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => preview.refetch()}
          data-testid="unified-select-release-breakdown-retry"
        >
          {t('availability.select.confirmReleaseBreakdown.retryBtn')}
        </Button>
      </div>
    );
  }

  const items = preview.data ?? [];
  if (items.length === 0) {
    return (
      <p
        className="text-sm text-muted-foreground"
        data-testid="unified-select-release-breakdown-empty"
      >
        {t('availability.select.confirmReleaseBreakdown.empty')}
      </p>
    );
  }

  const groups = groupReleasePreviewItems(items);

  return (
    <div className="space-y-2" data-testid="unified-select-release-breakdown">
      <div className="flex flex-wrap gap-2" data-testid="unified-select-release-breakdown-groups">
        {groups.map((group) => (
          <BreakdownGroupChip
            key={group.outcome}
            testId={`unified-select-release-breakdown-group-${group.outcome}`}
            variant={RELEASE_BREAKDOWN_BADGE_VARIANT[group.outcome]}
            label={looseT(RELEASE_BREAKDOWN_LABEL_KEY[group.outcome])}
            count={distinctUnitCount(group.entries)}
          />
        ))}
      </div>

      {groups.map((group) => (
        <OutcomeResultBucket<BatchReleasePreviewItem>
          key={group.outcome}
          trigger={
            <span className="text-sm font-medium">
              {looseT(RELEASE_BREAKDOWN_LABEL_KEY[group.outcome])}
            </span>
          }
          entries={group.entries}
          emptyLabel=""
          testIdPrefix="unified-select-release-breakdown-entry"
          renderEntry={(entry) => {
            const { from, to } = resolveEntryDisplayRange(entry);
            return (
              <OutcomeResultEntryRow
                unit={lookup.get(entry.mediaUnitId)}
                dateFrom={from}
                dateTo={to}
                detail={
                  entry.outcome === 'unresolvable'
                    ? releaseBreakdownUnresolvableDetail(looseT)
                    : releaseBreakdownRemainingDetail(entry, looseT)
                }
                unknownUnitLabel={unknownUnitLabel}
              />
            );
          }}
        />
      ))}
    </div>
  );
}
